import { useEffect, useState } from "react";
import type { AuthClient, MeResponse } from "@saas-on-sheets/auth-client";
import {
  HelloAppApiError,
  type HelloAppClient,
  type ListResult,
  type SubmitInput,
  type SubmitResult,
} from "../api/helloAppClient.ts";
import { SubmitForm } from "../components/SubmitForm.tsx";
import { SubmissionsList } from "../components/SubmissionsList.tsx";
import { SheetDriveLinks } from "../components/SheetDriveLinks.tsx";
import { LineLinkCard } from "../components/LineLinkCard.tsx";
import { Banner } from "../components/Banner.tsx";
import { SubmissionsTabs, type SubmissionsTab } from "../components/SubmissionsTabs.tsx";
import { FilesList } from "../components/FilesList.tsx";
import { env } from "../env.ts";
import { useFocusRefetch } from "../hooks/useFocusRefetch.ts";

interface DashboardPageProps {
  me: MeResponse;
  authClient: AuthClient;
  appClient: HelloAppClient;
  onLogout: () => void;
  onRefreshMe: () => Promise<void>;
}

type DashState =
  | { kind: "loading" }
  | { kind: "no-access"; reason: string }
  | { kind: "ready"; list: ListResult }
  | { kind: "error"; message: string };

const NO_ACCESS_COPY: Record<string, string> = {
  no_subscription_for_app: "You're signed in but don't have access to hello-app yet.",
  no_org: "Your account isn't a member of any organization yet — ask your admin to invite you.",
  expired: "Your hello-app subscription has expired.",
  revoked: "Your hello-app access has been revoked. Please contact support.",
  upstream_blocked:
    "Couldn't reach the hello-app service from your network. Try again — if it keeps failing, contact support.",
};

function initials(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

export function DashboardPage({
  me,
  authClient,
  appClient,
  onLogout,
  onRefreshMe,
}: DashboardPageProps) {
  const [state, setState] = useState<DashState>({ kind: "loading" });
  const [tab, setTab] = useState<SubmissionsTab>("all");
  const [initialized, setInitialized] = useState(false);

  async function refresh(): Promise<void> {
    try {
      if (!initialized) {
        await appClient.init();
        setInitialized(true);
      }
      const list = await appClient.list();
      setState({ kind: "ready", list });
    } catch (err) {
      if (err instanceof HelloAppApiError && err.status === 403) {
        setState({ kind: "no-access", reason: err.reason });
        return;
      }
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to load submissions",
      });
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusRefetch(async () => {
    await onRefreshMe();
    await refresh();
  });

  async function handleSubmit(input: SubmitInput): Promise<SubmitResult> {
    return appClient.submit(input);
  }

  async function handleLogout(): Promise<void> {
    try {
      await authClient.logout();
    } finally {
      onLogout();
    }
  }

  return (
    <div className="dashboard">
      <header className="topbar">
        <div className="topbar__inner">
          <div className="topbar__brand">
            <span className="topbar__logo" aria-hidden="true">
              h
            </span>
            <span>hello-app</span>
          </div>
          <div className="topbar__user">
            <span className="user-pill" title={me.email}>
              <span className="user-pill__avatar" aria-hidden="true">
                {initials(me.email)}
              </span>
              <span className="user-pill__email">{me.email}</span>
            </span>
            <button type="button" className="btn btn--ghost" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard__content">
        <div className="dashboard__heading">
          <h1>Submissions</h1>
          <p>Create, view, and download submissions backed by Google Sheets &amp; Drive.</p>
        </div>

        {state.kind === "loading" ? (
          <div aria-live="polite" aria-busy="true">
            <div className="skeleton skeleton-row" />
            <div className="skeleton skeleton-row" />
            <div className="skeleton skeleton-row" />
          </div>
        ) : null}

        {state.kind === "no-access" ? (
          <section className="no-access" aria-labelledby="no-access-title">
            <h2 id="no-access-title">No access</h2>
            <Banner kind="error">
              {NO_ACCESS_COPY[state.reason] ?? `Access denied: ${state.reason}`}
            </Banner>
            <p>
              To get access, ask your administrator to add you as a member with an active
              subscription — then refresh this page.
            </p>
            <div className="no-access__actions">
              <button type="button" className="btn btn--primary" onClick={refresh}>
                Try again
              </button>
            </div>
          </section>
        ) : null}

        {state.kind === "error" ? <Banner kind="error">{state.message}</Banner> : null}

        {state.kind === "ready" ? (
          <>
            <section className="card" aria-labelledby="submit-title">
              <div className="card__header">
                <h2 id="submit-title">New submission</h2>
              </div>
              <SubmitForm onSubmit={handleSubmit} onSubmitted={refresh} />
            </section>

            {(() => {
              const filesOnly = state.list.rows.filter((r) => r.fileUrl && r.fileUrl.length > 0);
              return (
                <section className="card" aria-labelledby="list-title">
                  <div className="card__header">
                    <h2 id="list-title">Recent submissions</h2>
                    <SheetDriveLinks
                      sheetUrl={state.list.sheetUrl}
                      driveFolderUrl={state.list.driveFolderUrl}
                    />
                  </div>
                  <SubmissionsTabs value={tab} onChange={setTab} fileCount={filesOnly.length} />
                  {tab === "all" ? (
                    <SubmissionsList rows={state.list.rows} />
                  ) : (
                    <FilesList rows={filesOnly} />
                  )}
                </section>
              );
            })()}

            <LineLinkCard
              lineLinked={me.lineLinked}
              {...(me.lineDisplayName !== undefined ? { lineDisplayName: me.lineDisplayName } : {})}
              {...(me.linePictureUrl !== undefined ? { linePictureUrl: me.linePictureUrl } : {})}
              {...(me.lineLinkedAt !== undefined ? { lineLinkedAt: me.lineLinkedAt } : {})}
              onGenerate={() => authClient.generateLineCode({ appId: env.VITE_APP_ID })}
              onUnlink={async () => {
                await authClient.unlinkLine({ appId: env.VITE_APP_ID });
              }}
              onRefresh={onRefreshMe}
            />
          </>
        ) : null}
      </main>
    </div>
  );
}
